import {
    type NavigationGuard,
    type RouteRecordRaw,
    type Router,
    type RouterHistory,
    createRouter as createVueRouter,
} from 'vue-router'

import { EXPORT_GAME_PAGE_CACHE_TIME, LISTING_PAGE_CACHE_TIME } from '@/constants/cache'
import { mapLocaleRouteParamName } from '@/i18n/helpers'
import type { I18NAddon } from '@/i18n/i18n'
import type { AvailableLocale } from '@/i18n/types'
import InitWrapper from '@/InitWrapper.vue'
import KidsInitWrapper from '@/pages/Kids/components/KidsInitWrapper.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { SSR_QUERY } from '@/router/query-params'
import { wrapQueryWithMarkers } from '@/router/server-utils'
import { type ImportWrapper, RedirectMechanism, RedirectReason } from '@/types'
import { isKidsHost } from '@/utils/is-kids-host'

// import router localization all at once eagerly, because we need to resolve hreflangs
import { type LocalizedPaths, allLocalizedPaths } from './localized-paths'
import { getKidsRoutes } from './white-label/kids'

const groupGameBeforeEnter: NavigationGuard = (to) => {
    const { [SSR_QUERY.PAGE]: page, ...queries } = to.query
    if (page === '1') {
        return {
            path: to.path,
            query: queries,
            replace: true,
            state: {
                statusCode: 301,
                headers: {
                    'X-BFF-Redirect-Reason': RedirectReason.FIRST_CATEGORY_PAGE,
                    'X-BFF-Redirect-Mechanism': RedirectMechanism.FRONTEND_ROUTER,
                },
            },
        }
    }
    return undefined
}

const createLocalizedRoutes = ({
    locale,
    host,
    registerImport,
}: {
    locale: AvailableLocale
    host: string
    registerImport: ImportWrapper
}): RouteRecordRaw[] => {
    const localizedPaths = allLocalizedPaths[locale]

    const getLocalizedPathName = (key: keyof LocalizedPaths) => localizedPaths[key]

    if (isKidsHost(host)) {
        return getKidsRoutes({ registerImport })
    }

    return [
        {
            path: 'oauth/consent',
            name: ROUTE_NAMES.PLAYGAMA_AUTH_CONSENT,
            component: registerImport(() => import('@/pages/Auth/PlaygamaOAuthConsent.vue')),
            meta: {
                noLayout: true,
                noLocaleInRoute: true,
                noIndex: true,
            },
        },
        {
            path: '',
            name: ROUTE_NAMES.MAIN,
            component: registerImport(() => import(`@/pages/Homepage/Homepage.vue`)),
            meta: {
                showDesktopStickyBanner: true,
                showMobileStickyBanner: true,
            },
        },
        {
            path: getLocalizedPathName('game'),
            name: ROUTE_NAMES.GAME_PAGE,
            component: registerImport(() => import(`@/pages/Games/GameRouteWrapper.vue`)),
            meta: {
                showMobileStickyBanner: true,
                noLayout: true,
                noRootManifest: true,
                noLazyHydration: true,
            },
        },
        {
            path: 'export/game/:game',
            name: ROUTE_NAMES.EXPORT_GAME_PAGE,
            component: registerImport(() => import(`@/pages/Games/ExportGame.vue`)),
            meta: {
                noLocaleInRoute: true,
                manuallyHandledErrors: true,
                noLayout: true,
                noLazyHydration: true,
                cache: {
                    sMaxageSeconds: EXPORT_GAME_PAGE_CACHE_TIME,
                },
            },
        },
        {
            path: 'widget/game/:game',
            name: ROUTE_NAMES.WIDGET_GAME_PAGE,
            component: registerImport(() => import(`@/pages/Games/WidgetGame.vue`)),
            meta: {
                noLocaleInRoute: true,
                manuallyHandledErrors: true,
                noLayout: true,
            },
        },
        {
            path: 'random',
            name: ROUTE_NAMES.RANDOM_GAME,
            component: registerImport(() => import(`@/pages/Games/RandomGame.vue`)),
            meta: {
                noLocaleInRoute: true,
                noIndex: true,
            },
        },
        {
            path: getLocalizedPathName('category'),
            name: ROUTE_NAMES.CATEGORY,
            component: registerImport(() => import(`@/pages/Category/CategoryPage.vue`)),
            beforeEnter: groupGameBeforeEnter,
            meta: {
                showMobileStickyBanner: true,
                noLayout: true,
                pageParameterIndexationAllowed: true,
                cache: {
                    sMaxageSeconds: LISTING_PAGE_CACHE_TIME,
                },
            },
        },
        {
            path: getLocalizedPathName('tag'),
            name: ROUTE_NAMES.TAG,
            component: registerImport(() => import(`@/pages/Category/CategoryPage.vue`)),
            beforeEnter: groupGameBeforeEnter,
            meta: {
                showMobileStickyBanner: true,
                noLayout: true,
                pageParameterIndexationAllowed: true,
                cache: {
                    sMaxageSeconds: LISTING_PAGE_CACHE_TIME,
                },
            },
        },
        {
            path: getLocalizedPathName('series'),
            name: ROUTE_NAMES.SERIES,
            component: registerImport(() => import(`@/pages/Category/CategoryPage.vue`)),
            beforeEnter: groupGameBeforeEnter,
            meta: {
                showMobileStickyBanner: true,
                noLayout: true,
                pageParameterIndexationAllowed: true,
                cache: {
                    sMaxageSeconds: LISTING_PAGE_CACHE_TIME,
                },
            },
        },
        {
            path: getLocalizedPathName('developer'),
            name: ROUTE_NAMES.DEVELOPER,
            component: registerImport(() => import(`@/pages/Category/CategoryPage.vue`)),
            beforeEnter: groupGameBeforeEnter,
            meta: {
                showMobileStickyBanner: true,
                noLayout: true,
                pageParameterIndexationAllowed: true,
            },
        },
        {
            path: getLocalizedPathName('collection'),
            name: ROUTE_NAMES.COLLECTION,
            component: registerImport(() => import(`@/pages/Collection/CollectionPage.vue`)),
            meta: {
                showMobileStickyBanner: true,
                noLayout: true,
                cache: {
                    sMaxageSeconds: LISTING_PAGE_CACHE_TIME,
                },
            },
        },
        {
            path: getLocalizedPathName('contacts'),
            name: ROUTE_NAMES.CONTACTS,
            component: registerImport(() => import(`@/pages/ContactUs/ContactUsPage.vue`)),
        },
        {
            path: getLocalizedPathName('profile'),
            name: ROUTE_NAMES.PROFILE,
            component: registerImport(() => import(`@/pages/UserProfile/UserProfile.vue`)),
            meta: {
                noIndex: true,
            },
        },
        {
            path: getLocalizedPathName('shop'),
            name: ROUTE_NAMES.SHOP,
            beforeEnter: (to, _from, next) => {
                next({
                    name: ROUTE_NAMES.PROFILE,
                    query: to.query,
                    hash: to.hash,
                    state: {
                        statusCode: 301,
                        headers: {
                            'X-BFF-Redirect-Reason': RedirectReason.SHOP_TO_PROFILE,
                            'X-BFF-Redirect-Mechanism': RedirectMechanism.FRONTEND_ROUTER,
                        },
                    },
                })
            },
            component: {},
        },
        {
            path: getLocalizedPathName('allCategories'),
            name: ROUTE_NAMES.ALL_CATEGORIES,
            component: registerImport(() => import('@/pages/GamesCategories.vue')),
        },
        {
            path: getLocalizedPathName('allTags'),
            name: ROUTE_NAMES.ALL_TAGS,
            component: registerImport(() => import('@/pages/GamesCategories.vue')),
        },
        {
            path: getLocalizedPathName('allSeries'),
            name: ROUTE_NAMES.ALL_SERIES,
            component: registerImport(() => import('@/pages/GamesCategories.vue')),
        },
        {
            path: getLocalizedPathName('myGames'),
            name: ROUTE_NAMES.MY_GAMES,
            beforeEnter: (to, _from, next) => {
                next({
                    name: ROUTE_NAMES.PROFILE,
                    params: to.params,
                    query: to.query,
                    hash: to.hash,
                    state: {
                        statusCode: 301,
                        headers: {
                            'X-BFF-Redirect-Reason': RedirectReason.MY_GAMES_TO_PROFILE,
                            'X-BFF-Redirect-Mechanism': RedirectMechanism.FRONTEND_ROUTER,
                        },
                    },
                })
            },
            component: {},
        },
        {
            path: getLocalizedPathName('search'),
            name: ROUTE_NAMES.SEARCH,
            component: registerImport(() => import('@/pages/Search/SearchPage.vue')),
            meta: {
                showDesktopStickyBanner: true,
                showMobileStickyBanner: true,
                noLayout: true,
                noIndex: true,
            },
        },
        {
            path: getLocalizedPathName('steamGames'),
            name: ROUTE_NAMES.STEAM_GAMES,
            component: registerImport(() => import('@/pages/SteamGames/SteamGamesPage.vue')),
            meta: {
                cache: {
                    sMaxageSeconds: 300,
                },
            },
        },
        {
            path: getLocalizedPathName('confidential'),
            name: ROUTE_NAMES.CONFIDENTIAL,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
        },
        {
            path: getLocalizedPathName('termsofuse'),
            name: ROUTE_NAMES.TERMS_OF_USE,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
        },
        {
            path: getLocalizedPathName('termsofuse_mcm'),
            name: ROUTE_NAMES.TERMS_OF_USE_MCM,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
        },
        {
            path: getLocalizedPathName('digitalServiceAct'),
            name: ROUTE_NAMES.DIGITAL_SERVICE_ACT,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
        },
        {
            path: getLocalizedPathName('leaderboardsInfo'),
            name: ROUTE_NAMES.LEADERBOARDS_INFO,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
        },
        {
            path: getLocalizedPathName('takedown_notice'),
            name: ROUTE_NAMES.TAKEDOWN_NOTICE,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
        },
        {
            path: getLocalizedPathName('license'),
            name: ROUTE_NAMES.LICENSE,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
        },
        {
            path: getLocalizedPathName('partners_api'),
            name: ROUTE_NAMES.PARTNERS_API,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
        },
        {
            path: 'about-us',
            name: ROUTE_NAMES.ABOUT_US,
            component: registerImport(() => import('@/pages/static/StaticPage.vue')),
            meta: {
                noLocaleInRoute: true,
            },
        },
        // English-only: Clause 18.7 of the referral terms makes the English text the only
        // binding version, so these carry no locale in the URL.
        {
            path: 'referral/publishers',
            name: ROUTE_NAMES.REFERRAL_PUBLISHERS,
            component: registerImport(() => import('@/pages/ReferralPublishers/ReferralLandingPage.vue')),
            meta: {
                noLocaleInRoute: true,
                noLayout: true,
                noPortalPrompts: true,
            },
        },
        {
            path: 'referral/publishers/apply',
            name: ROUTE_NAMES.REFERRAL_PUBLISHERS_APPLY,
            component: registerImport(() => import('@/pages/ReferralPublishers/ReferralApplyPage.vue')),
            meta: {
                noLocaleInRoute: true,
                noLayout: true,
                noPortalPrompts: true,
                noIndex: true,
                // The sign-in modal and the terms-box measurement must run at load, not at idle.
                noLazyHydration: true,
            },
        },
        {
            path: 'referral/publishers/terms',
            name: ROUTE_NAMES.REFERRAL_PUBLISHERS_TERMS,
            component: registerImport(() => import('@/pages/ReferralPublishers/ReferralTermsPage.vue')),
            meta: {
                noLocaleInRoute: true,
                noLayout: true,
                noPortalPrompts: true,
            },
        },
        {
            path: 'referral/publishers/thank-you',
            name: ROUTE_NAMES.REFERRAL_PUBLISHERS_THANK_YOU,
            component: registerImport(() => import('@/pages/ReferralPublishers/ReferralThankYouPage.vue')),
            meta: {
                noLocaleInRoute: true,
                noLayout: true,
                noPortalPrompts: true,
                noIndex: true,
            },
        },
        {
            // this route is for rendering 5xx errors
            // the error itself is rendered by InitWrapper, so here the component is a dummy
            path: 'error',
            component: {},
            meta: {
                noIndex: true,
            },
        },
        {
            path: ':catchAll(.*)',
            component: registerImport(() => import(`@/pages/Error/NotFoundPage.vue`)),
        },
    ]
}

// Update `children` when changing locale
const updateLocalizedRoutes = ({
    router,
    registerImport,
    locale,
    host,
}: {
    router: Router
    registerImport: ImportWrapper
    locale: AvailableLocale
    host: string
}) => {
    const parentRoute = router.getRoutes().find((route) => route.name === ROUTE_NAMES.ROOT)
    if (parentRoute) {
        parentRoute.children.forEach((child) => {
            if (child.name) {
                router.removeRoute(child.name)
            }
        })

        const newChildren = createLocalizedRoutes({ locale, host, registerImport })
        newChildren.forEach((child) => router.addRoute(ROUTE_NAMES.ROOT, child))
    }
}

// Create routers for all supported locales
function createRoutersForAllLocales({
    history,
    i18nAddon,
    host,
}: {
    history: RouterHistory
    i18nAddon: I18NAddon
    host: string
}) {
    const result = {} as Record<AvailableLocale, Router>

    for (const locale of i18nAddon.getSupportedLocales()) {
        result[locale] = createRouterForLocale({ locale, history, i18nAddon, host })
    }

    return result
}

const getRoutes = ({
    i18nAddon,
    registerImport,
    host,
}: {
    i18nAddon: I18NAddon
    registerImport: ImportWrapper
    host: string
}): RouteRecordRaw[] => {
    const supportedLocales = i18nAddon.getSupportedLocales()
    return [
        {
            path: `/:locale(${supportedLocales.map(mapLocaleRouteParamName).join('|')})?/`,
            name: ROUTE_NAMES.ROOT,
            component: isKidsHost(host) ? KidsInitWrapper : InitWrapper,
            // create default EN router to match the locale param on initial navigation
            children: createLocalizedRoutes({ locale: 'en-US', registerImport, host }),
        },
    ]
}

const createRouterForLocale = ({
    locale,
    history,
    i18nAddon,
    host,
}: {
    locale: AvailableLocale
    history: RouterHistory
    i18nAddon: I18NAddon
    host: string
}): Router => {
    // Create routes for this specific locale
    const routes = [
        {
            path: locale === i18nAddon.getDefaultLocale() ? '/' : `/${mapLocaleRouteParamName(locale)}/`,
            name: ROUTE_NAMES.ROOT,
            component: {},
            children: createLocalizedRoutes({ locale, registerImport: (cb) => cb, host }),
        },
    ]

    // Create a router instance for this locale
    return createVueRouter({
        routes,
        history,
        // We don't need scroll behavior for these routers as they're just for meta tags
    })
}

export default function createRouter({
    history,
    registerImport,
    i18nAddon,
    deferredInit = Promise.resolve(),
    host,
}: {
    history: RouterHistory
    registerImport: ImportWrapper
    i18nAddon: I18NAddon
    deferredInit?: Promise<void>
    host: string
}) {
    // Create the main router that will be used for navigation
    const router = createVueRouter({
        routes: getRoutes({ i18nAddon, registerImport, host }),
        history,
        scrollBehavior: async (to, _, savedPosition) => {
            /*
            Before activate scroll behavior we should make sure that we mounted vue app
            */
            await deferredInit
            if (to.hash) {
                return {
                    el: to.hash,
                }
            }
            if (savedPosition) {
                return savedPosition
            }

            return { top: 0 }
        },
        linkActiveClass: '', // to prevent set  router-link-active on RouterLinks
        linkExactActiveClass: '', // to prevent set  router-link-exact-active on RouterLinks
    })

    const defaultLocale = i18nAddon.getDefaultLocale()
    router.beforeEach(async (to, _, next) => {
        if (import.meta.env.SSR) {
            to.query = wrapQueryWithMarkers(to.query)
        }

        const paramLocale = (to.params.locale as string) || ''
        const currentRouteLocale = mapLocaleRouteParamName(paramLocale) || defaultLocale

        const currentLocale = i18nAddon.currentLocale.value
        const preferredLocale = i18nAddon.getPreferredLocale()
        let localeToLoad = currentRouteLocale

        // 1. if route requires no locale param in url, handle it
        if (to.name && to.meta.noLocaleInRoute) {
            if (paramLocale !== '') {
                // if have a locale, remove it with permanent 301
                return next({
                    name: to.name,
                    params: { ...to.params, locale: '' },
                    query: to.query,
                    state: {
                        statusCode: 301,
                        headers: {
                            'X-BFF-Redirect-Reason': RedirectReason.NO_LOCALE_IN_ROUTE,
                            'X-BFF-Redirect-Mechanism': RedirectMechanism.FRONTEND_ROUTER,
                        },
                    },
                })
            }

            localeToLoad = preferredLocale || currentRouteLocale

            // 2. redirect to preferredLocale if possible
        } else {
            if (
                paramLocale === '' && // only do redirect if the locale is omitted
                preferredLocale && // if we detected a locale from cookie or accept-language header
                preferredLocale !== currentRouteLocale && // if it's not the same as the one in the URL
                to.name // we can only redirect once it is parsed against it's corresponding localized router
            ) {
                const nextLocale = preferredLocale === defaultLocale ? '' : mapLocaleRouteParamName(preferredLocale) // remove default locale from URL
                return next({
                    name: to.name,
                    params: { ...to.params, locale: nextLocale },
                    query: to.query,
                    state: {
                        headers: {
                            'X-BFF-Redirect-Reason': RedirectReason.PREFERRED_LOCALE,
                            'X-BFF-Redirect-Mechanism': RedirectMechanism.FRONTEND_ROUTER,
                        },
                    },
                })
                // always redirect from '/en' to '/' with permanent 301
            } else if (paramLocale === mapLocaleRouteParamName(defaultLocale)) {
                return next({
                    name: to.name,
                    params: { ...to.params, locale: '' },
                    query: to.query,
                    state: {
                        statusCode: 301,
                        headers: {
                            'X-BFF-Redirect-Reason': RedirectReason.REMOVE_EN_LOCALE,
                            'X-BFF-Redirect-Mechanism': RedirectMechanism.FRONTEND_ROUTER,
                        },
                    },
                })
            }
        }

        // 3. usual navigation
        if (localeToLoad === currentLocale) {
            return next()
        }

        // 4. first navigation with new locale
        const loadedLocale = await i18nAddon.loadLocale({ locale: localeToLoad, registerImport })
        updateLocalizedRoutes({ router, registerImport, locale: loadedLocale, host })
        return next(to)
    })

    // Create routers for all supported locales (for meta tags and other seo purposes)
    const routersByLocale: Record<AvailableLocale, Router> = createRoutersForAllLocales({
        history,
        i18nAddon,
        host,
    })

    return { router, routersByLocale }
}
